import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { IconButton } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { FormattedMessage } from 'react-intl';
import { ToastsStack, UndoAlert, UndoButton } from './UndoToasts.styled';

const UNDO_TOAST_DURATION = 20000;

interface UndoToastOptions {
  message: ReactNode;
  onUndo: () => void;
}

interface UndoToastEntry extends UndoToastOptions {
  id: number;
}

interface UndoToastsContextValue {
  pushUndoToast: (options: UndoToastOptions) => void;
  clearUndoToasts: () => void;
}

const UndoToastsContext = createContext<UndoToastsContextValue>({
  pushUndoToast: () => undefined,
  clearUndoToasts: () => undefined,
});

export const useUndoToasts = () => useContext(UndoToastsContext);

function UndoToastItem({
  toast,
  onClose,
}: {
  toast: UndoToastEntry;
  onClose: (id: number) => void;
}) {
  useEffect(() => {
    const timer = setTimeout(() => onClose(toast.id), UNDO_TOAST_DURATION);
    return () => clearTimeout(timer);
  }, [toast.id, onClose]);

  const handleUndo = () => {
    toast.onUndo();
    onClose(toast.id);
  };

  return (
    <UndoAlert
      severity="success"
      action={
        <>
          <UndoButton size="small" onClick={handleUndo}>
            <FormattedMessage
              id="pages.scorecards.edit.undo"
              defaultMessage="Undo"
            />
          </UndoButton>
          <IconButton
            size="small"
            color="inherit"
            aria-label="close"
            onClick={() => onClose(toast.id)}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </>
      }
    >
      {toast.message}
    </UndoAlert>
  );
}

export default function UndoToastsProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [toasts, setToasts] = useState<UndoToastEntry[]>([]);
  const nextIdRef = useRef(0);

  const pushUndoToast = useCallback((options: UndoToastOptions) => {
    nextIdRef.current += 1;
    setToasts((prev) => [...prev, { ...options, id: nextIdRef.current }]);
  }, []);

  const clearUndoToasts = useCallback(() => {
    setToasts([]);
  }, []);

  const closeToast = useCallback((id: number) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const contextValue = useMemo(
    () => ({ pushUndoToast, clearUndoToasts }),
    [pushUndoToast, clearUndoToasts],
  );

  return (
    <UndoToastsContext.Provider value={contextValue}>
      {children}
      {toasts.length > 0 && (
        <ToastsStack>
          {toasts.map((toast) => (
            <UndoToastItem key={toast.id} toast={toast} onClose={closeToast} />
          ))}
        </ToastsStack>
      )}
    </UndoToastsContext.Provider>
  );
}
